import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CreditCard, Receipt, Sparkles, Loader2, IndianRupee, CalendarClock, Users, UserPlus, X, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 40.8 — dedicated "Upgrade & Billing" screen accessible from the
// profile menu. Shows current plan snapshot at the top plus two clear entry
// points (Manage Plan / Payment History) so users have a single destination.
export default function BillingPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const authHeaders = () => {
    const t = localStorage.getItem('token');
    const base = { 'Content-Type': 'application/json' };
    return t ? { ...base, Authorization: `Bearer ${t}` } : base;
  };

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API}/api/subscription/status`, { credentials: 'include', headers: authHeaders() });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
  };

  const loadFamily = async () => {
    try {
      const r = await fetch(`${API}/api/subscription/family`, { credentials: 'include', headers: authHeaders() });
      if (r.ok) setFamily(await r.json());
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadStatus(), loadFamily()]);
      setLoading(false);
    })();
  }, []);

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error('Enter a sibling email');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/subscription/family/invite`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Added ${data.added?.email || 'sibling'} to your Family Plan`);
        setInviteEmail('');
        await loadFamily();
      } else {
        toast.error(data.detail || 'Could not add sibling');
      }
    } catch { toast.error('Network error'); }
    setBusy(false);
  };

  const removeSibling = async (uid) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/subscription/family/remove`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ user_id: uid }),
      });
      if (r.ok) { toast.success('Sibling removed'); await loadFamily(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setBusy(false);
  };

  const leaveFamily = async () => {
    if (!window.confirm('Leave this Family Plan? You will lose Pro access unless you have your own subscription.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/subscription/family/leave`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      if (r.ok) { toast.success('You have left the Family Plan'); await loadFamily(); await loadStatus(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setBusy(false);
  };

  const isActive = status && (status.state === 'active' || status.subscription_active);
  const isTrial = status && status.state === 'trial';
  const expiresOn = status?.subscription_expires_at || status?.trial_ends_at;
  const isFamilyOwner = family?.role === 'owner';
  const isFamilyMember = family?.role === 'member';
  const currentPlanLabel = status?.subscription_plan === 'family'
    ? 'CognitiaIndia Family'
    : isActive ? 'CognitiaIndia Pro' : isTrial ? '7-day Free Trial' : 'Free plan';

  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="billing-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Upgrade & Billing</h1>
            <p className="text-sm text-muted-foreground">Manage your subscription and view every payment receipt in one place.</p>
          </div>
        </div>

        {/* Current plan snapshot */}
        <Card className="mb-4 border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30" data-testid="billing-current-plan">
          <CardContent className="p-5">
            {loading ? (
              <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading current plan…</div>
            ) : (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-wide font-bold text-indigo-500 mb-1">Current plan</p>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-black" data-testid="billing-plan-name">
                      {currentPlanLabel}
                    </h2>
                    {isActive && <Badge className="bg-emerald-500 text-white">Active</Badge>}
                    {isTrial && <Badge className="bg-amber-500 text-white">Trial</Badge>}
                    {!isActive && !isTrial && <Badge variant="outline">Not subscribed</Badge>}
                    {isFamilyOwner && <Badge className="bg-purple-500 text-white">Family owner</Badge>}
                    {isFamilyMember && <Badge className="bg-purple-500 text-white">Family member</Badge>}
                  </div>
                  {expiresOn && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="billing-expires">
                      <CalendarClock className="w-3 h-3" />
                      {isActive ? 'Renews / expires on' : 'Expires on'} {new Date(expiresOn).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase text-muted-foreground">Standard price</p>
                  <p className="text-2xl font-black flex items-center gap-1"><IndianRupee className="w-5 h-5" />399<span className="text-xs font-medium text-muted-foreground">/month</span></p>
                  <p className="text-[10px] text-muted-foreground">Family ₹599/mo · Yearly ₹4,453</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Two entry points */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/subscription')} data-testid="billing-open-plans">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl bg-indigo-500/15 flex items-center justify-center mb-3">
                <CreditCard className="w-6 h-6 text-indigo-500" />
              </div>
              <h3 className="text-lg font-bold mb-1">Manage Plan</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {isActive ? 'Renew, switch between monthly and yearly, or cancel.' : isTrial ? 'Convert your trial into a paid subscription and unlock everything.' : 'Start your 7-day free trial — no credit card charged upfront.'}
              </p>
              <Button size="sm" className="w-full" data-testid="billing-cta-plans">
                <Sparkles className="w-4 h-4 mr-1.5" />{isActive ? 'View subscription' : isTrial ? 'Subscribe now' : 'See plans'}
              </Button>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/payment-history')} data-testid="billing-open-history">
            <CardContent className="p-5">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-3">
                <Receipt className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-bold mb-1">Payment History</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Every verified payment with instant PDF and HTML receipt downloads for expenses or reimbursements.
              </p>
              <Button size="sm" variant="outline" className="w-full" data-testid="billing-cta-history">
                <Receipt className="w-4 h-4 mr-1.5" />View receipts
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Family Plan manager */}
        {(isFamilyOwner || isFamilyMember || (!isFamilyOwner && !isFamilyMember)) && (
          <Card className="mt-4 border-purple-200 dark:border-purple-900" data-testid="family-plan-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Family Plan</h3>
                    <p className="text-xs text-muted-foreground">₹599/month · up to {family?.max_seats || 3} siblings share Pro access</p>
                  </div>
                </div>
                {!isFamilyOwner && !isFamilyMember && (
                  <Button size="sm" onClick={() => navigate('/subscription')} data-testid="family-upgrade-btn">
                    <Sparkles className="w-4 h-4 mr-1.5" />Upgrade to Family
                  </Button>
                )}
              </div>

              {isFamilyOwner && (
                <div data-testid="family-owner-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-purple-500 text-white">{family.used_seats}/{family.max_seats} seats used</Badge>
                    <Badge variant="outline">Owner</Badge>
                  </div>

                  {family.siblings?.length > 0 ? (
                    <ul className="space-y-1.5 mb-3" data-testid="family-siblings-list">
                      {family.siblings.map((s) => (
                        <li key={s.user_id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border" data-testid={`family-sibling-${s.user_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{s.name || s.email}</p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              {s.email}{s.class_level ? ` · Class ${s.class_level}` : ''}{s.board ? ` · ${s.board}` : ''}
                            </p>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => removeSibling(s.user_id)} disabled={busy} className="text-red-500 hover:text-red-600" data-testid={`family-remove-${s.user_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground mb-3">No siblings added yet. Invite one below to fill your remaining seats.</p>
                  )}

                  {family.used_seats < family.max_seats && (
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="sibling@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm"
                        data-testid="family-invite-input"
                      />
                      <Button onClick={invite} disabled={busy} data-testid="family-invite-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" />Add sibling</>}
                      </Button>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-2">
                    They must already have a CognitiaIndia account (free signup is fine). They will start receiving Pro access instantly.
                  </p>
                </div>
              )}

              {isFamilyMember && (
                <div data-testid="family-member-panel">
                  <p className="text-sm mb-2">
                    You are on <b>{family.owner_email || 'a family member'}</b>&apos;s Family Plan
                    {family.expires_at ? <> · valid until {new Date(family.expires_at).toLocaleDateString()}</> : null}.
                  </p>
                  <Button size="sm" variant="outline" onClick={leaveFamily} disabled={busy} data-testid="family-leave-btn">
                    <LogOut className="w-3.5 h-3.5 mr-1" />Leave Family Plan
                  </Button>
                </div>
              )}

              {!isFamilyOwner && !isFamilyMember && (
                <div className="text-xs text-muted-foreground">
                  Cover up to 3 kids for ₹599/month — that&apos;s ₹200 per sibling, cheaper than three separate monthlies. Parents get a single dashboard view across every child.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          All prices in INR and include 18% GST. Payments are handled securely by Razorpay.
        </p>
      </div>
    </div>
  );
}
